/**
 * App orchestrator. Owns mode transitions (idle -> preview -> navigating),
 * wires GPS fixes into the navigator and camera, and connects the UI panels.
 */

import './style.css';
import { registerSW } from 'virtual:pwa-register';

import { APP_VERSION, hasMapbox } from './core/config';
import { getLang, t } from './core/i18n';
import { store, select } from './core/store';
import { activeVehicle } from './core/settings';
import type { Fix, LngLat, Place, Route } from './core/types';
import { cumulativeDistances, distance } from './core/geo';
import { formatDistanceShort } from './core/format';
import { addRecent, listTrips, saveTrip } from './core/db';

import { GpsWatcher } from './nav/gps';
import { fetchRouteAlternatives, fetchRoutes } from './nav/directions';
import { RouteNavigator } from './nav/navigator';
import { TripRecorder } from './nav/tripRecorder';
import { computeRouteStats, statsFor, type RouteStats } from './nav/routeStats';
import { applyPace, tierForStep } from './nav/paceModel';
import { roadLabel } from './nav/roads';
import { learnedVias } from './nav/learnedRoutes';
import { buildRoughIndex, roughnessOfRoute, roughAhead, type RoughIndex, type RouteRoughness } from './nav/roughMap';
import { RoughnessMeter } from './nav/roughness';
import { readPoint } from './nav/tripRecorder';
import { enableWakeLock, disableWakeLock } from './nav/wakelock';
import { reverseGeocode, reverseLocality } from './search/geocode';
import { adviceKey, fetchAdvice, type RouteAdvice } from './ai/advisor';
import { Fleet, type MessageKind, type QuickMessage } from './fleet/fleet';
import { findFuelStopsAhead, etaToStop, type FuelStop } from './nav/fuelStops';
import { analyseElevation, type ElevationProfile } from './nav/elevation';

import { MapView } from './map/mapView';
import { ManeuverCard } from './ui/maneuverCard';
import { NavHud } from './ui/navHud';
import { SearchPanel } from './ui/searchPanel';
import { RoutePreview } from './ui/routePreview';
import { SettingsPanel } from './ui/settingsPanel';
import { TripsPanel } from './ui/tripsPanel';
import { SpotifyBar } from './ui/spotifyBar';
import { FleetBubble } from './ui/fleetBubble';
import { FuelBubble, type FuelChoice } from './ui/fuelBubble';
import { RouteInfoSheet } from './ui/routeInfo';
import { LiveDataPanel } from './ui/liveData';

import { announce, primeSpeech, setDucker, stopSpeaking } from './audio/voice';
import { SpotifyPlayer, completeLoginIfReturning, isAuthed } from './audio/spotify';

import { LoginView } from './auth/loginView';
import * as authApi from './auth/api';
import type { SavedPlace, User } from './auth/api';

registerSW({ immediate: true });

const appEl = document.getElementById('app')!;
const uiEl = document.getElementById('ui')!;
const mapEl = document.getElementById('map')!;

/**
 * Startup must never fail silently to a black screen. Any uncaught error
 * before the UI exists gets rendered full-screen so the failure is readable
 * on a phone or head unit with no devtools.
 */
function fatalScreen(title: string, detail: string): void {
  uiEl.innerHTML = `<div class="fatal"><h1>${title}</h1><p>${detail}</p>
    <button class="btn btn--primary" onclick="location.reload()">Reload</button></div>`;
}

let bootFinished = false;
let bootStartedAt = 0;

/**
 * Startup breadcrumbs, made impossible to miss: every step is mirrored into
 * the tab title and a small on-screen strip. On a healthy boot the strip
 * exists for under a second; on a stall it stays, telling us the exact step.
 */
const bootTrace: string[] = [];
let traceEl: HTMLElement | null = null;

function trace(step: string): void {
  bootTrace.push(step);
  document.title = `WF·${step}`;
  if (!traceEl) {
    traceEl = document.createElement('div');
    traceEl.className = 'boot-trace';
    document.body.appendChild(traceEl);
  }
  traceEl.textContent = bootTrace.join(' → ');
}

function traceDone(): void {
  bootFinished = true;
  document.title = 'Wayfarer Navigation';
  traceEl?.remove();
  traceEl = null;
}

function traceText(): string {
  return `Progress: ${bootTrace.join(' → ') || '(nothing ran)'}`;
}

// Watch continuously: once boot() has started, it must finish or error within
// 15 s - otherwise show the stall screen with the breadcrumbs.
window.setInterval(() => {
  if (!bootFinished && bootStartedAt && Date.now() - bootStartedAt > 15_000 && !document.querySelector('.fatal')) {
    fatalScreen('Startup stalled', traceText());
  }
}, 5_000);

/**
 * Keep every device on the latest deploy.
 *
 * This used to set a one-shot `wf-sw-reloaded` flag: the app reloaded for the
 * FIRST service-worker takeover of a tab session and never again. A phone
 * running this as a PWA keeps the same session alive for days, so the second
 * and every later deploy was fetched and then ignored - the driver kept
 * running an old bundle and reported bugs that had already been fixed.
 *
 * The flag is now a timestamp, which still prevents a reload loop (two
 * takeovers seconds apart) but lets every genuine update through. And because
 * a long-lived PWA may never navigate, we actively poll for a new worker on
 * resume and on a timer, rather than waiting for one to appear by luck.
 */
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    const last = Number(sessionStorage.getItem('wf-sw-reloaded') ?? 0);
    if (Date.now() - last > 10_000) {
      sessionStorage.setItem('wf-sw-reloaded', String(Date.now()));
      location.reload();
    }
  });

  void navigator.serviceWorker.ready.then((reg) => {
    const check = (): void => void reg.update().catch(() => {});
    // Never mid-drive: a reload during navigation would drop the route.
    const checkWhenIdle = (): void => {
      if (store.get().mode === 'idle') check();
    };
    setInterval(checkWhenIdle, 15 * 60_000);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) checkWhenIdle();
    });
    check();
  });
}

window.addEventListener('error', (e) => {
  if (!bootFinished) {
    fatalScreen(
      'Wayfarer could not start',
      `${String(e.error?.message ?? e.message)}<br/><small>${traceText()}</small>`,
    );
  }
});
window.addEventListener('unhandledrejection', (e) => {
  if (!bootFinished) {
    fatalScreen(
      'Wayfarer could not start',
      `${String((e.reason as Error)?.message ?? e.reason)}<br/><small>${traceText()}</small>`,
    );
  }
});

/** Mapbox GL v3 needs WebGL2; without it (hardware acceleration off, old GPU
 *  driver, some head units) the Map constructor throws. Check up front so the
 *  driver gets an actionable message instead of a black screen. */
function webgl2Available(): boolean {
  try {
    const canvas = document.createElement('canvas');
    return canvas.getContext('webgl2') !== null;
  } catch {
    return false;
  }
}

/* ------------------------------------------------------------------------- */
/* Auth gate                                                                  */
/* ------------------------------------------------------------------------- */

let currentUser: User | null = null;

async function gate(): Promise<void> {
  trace('gate');
  if (!hasMapbox()) {
    uiEl.innerHTML = `<div class="fatal">
      <h1>Wayfarer</h1>
      <p>No Mapbox token configured. Copy <code>.env.example</code> to <code>.env.local</code>,
      paste your free token from account.mapbox.com, and rebuild.</p></div>`;
    return;
  }
  try {
    const { user } = await authApi.me();
    trace(user ? `me:${user.status}` : 'me:none');
    if (user && user.status === 'approved') {
      currentUser = user;
      void boot();
      return;
    }
  } catch {
    /* no backend reachable - fall through to login, which will show the error */
  }
  trace('login:shown');
  const login = new LoginView(uiEl, (user) => {
    trace('login:authed');
    currentUser = user;
    login.destroy();
    void boot();
  });
}

/* ------------------------------------------------------------------------- */
/* Main app                                                                   */
/* ------------------------------------------------------------------------- */

async function boot(): Promise<void> {
  bootStartedAt = Date.now();
  trace('boot');
  if (!webgl2Available()) {
    fatalScreen(
      'Map engine unavailable',
      'This browser has no WebGL2, which the map needs. Usually this means ' +
        'hardware acceleration is turned OFF: open Chrome Settings → System → ' +
        'enable "Use graphics acceleration when available", relaunch the browser, ' +
        'and reload this page. (chrome://gpu shows the current state.)',
    );
    return;
  }

  /* ------------------------------ theme ------------------------------- */
  const resolveTheme = (): 'night' | 'day' => {
    const pref = store.get().settings.theme;
    if (pref !== 'auto') return pref;
    const h = new Date().getHours() + new Date().getMinutes() / 60;
    return h >= 6.25 && h < 18.5 ? 'day' : 'night';
  };

  /* ------------------------------- map -------------------------------- */
  trace('webgl:ok');
  const { loadStrippedStyle } = await import('./map/styleLoader');
  trace('style:fetch');
  const bootStyle = await loadStrippedStyle(resolveTheme());
  trace(typeof bootStyle === 'string' ? 'style:fallback' : 'style:ok');
  const mapView = new MapView(mapEl, bootStyle, {
    onLongPress: (point) => void pickDroppedPin(point),
    onAltRouteClick: (routeId) => {
      const idx = store.get().routes.findIndex((r) => r._id === routeId);
      if (idx >= 0) selectRoute(idx);
    },
    onUserGesture: () => {
      // Breaking follow does not by itself warrant the button - it appears
      // once the vehicle has actually drifted off screen.
      refreshRecenter();
    },
    onPlaceClick: (marker) => {
      if (store.get().mode === 'navigating') return;
      void pickDestination({
        id: `saved:${marker.id}`,
        name: marker.name,
        address: `Saved by ${marker.creatorName}`,
        center: [marker.lng, marker.lat],
        kind: 'saved',
      });
    },
  });

  trace('map:ok');
  /* -------------------------- shared saved places ----------------------- */
  let sharedPlaces: SavedPlace[] = [];

  async function loadPlaces(): Promise<void> {
    try {
      sharedPlaces = (await authApi.fetchPlaces()).places;
      mapView.setPlaces(sharedPlaces);
    } catch {
      /* offline or backend down - markers just don't refresh */
    }
  }
  void loadPlaces();
  // Shared locations from other drivers should appear without a reload.
  setInterval(() => void loadPlaces(), 90_000);

  function isDestSaved(): boolean {
    const dest = store.get().destination;
    if (!dest) return false;
    return sharedPlaces.some((p) => distance([p.lng, p.lat], dest.center) < 30);
  }

  let lastTheme = resolveTheme();
  setInterval(() => {
    const t = resolveTheme();
    if (t !== lastTheme) {
      lastTheme = t;
      void mapView.setTheme(t);
    }
  }, 60_000);
  select(
    (s) => s.settings.theme,
    () => {
      const t = resolveTheme();
      if (t !== lastTheme) {
        lastTheme = t;
        void mapView.setTheme(t);
      }
    },
    { immediate: false },
  );

  /* ------------------------------ Spotify ------------------------------ */
  const spotifyPlayer = new SpotifyPlayer({
    onState: (s) => spotifyBar.update(s),
  });
  setDucker(spotifyPlayer);
  void completeLoginIfReturning().then((returned) => {
    if (returned || isAuthed()) void spotifyPlayer.init();
  });

  trace('spotify:ok');
  /* -------------------------------- UI --------------------------------- */
  const maneuverCard = new ManeuverCard(uiEl);

  /*
   * Keep the right-hand control rail below the sign.
   *
   * The card grows and shrinks - lane chips, a lane-trap warning, a second
   * road on the strip - so any fixed offset for the buttons beside it is wrong
   * some of the time. Measuring it is the only version that cannot overlap.
   */
  function measureRail(): void {
    // In landscape the card is beside the rail rather than above it, so the
    // rail starts at the top. An inline style beats the stylesheet, so this
    // has to zero it out itself rather than leaving it to the media query.
    const landscape = window.innerWidth > window.innerHeight && window.innerHeight < 620;
    const h =
      landscape || maneuverCard.el.hidden ? 0 : maneuverCard.el.getBoundingClientRect().height;
    document.documentElement.style.setProperty('--card-h', `${Math.round(h)}px`);
  }
  new ResizeObserver(measureRail).observe(maneuverCard.el);
  window.addEventListener('resize', measureRail);
  window.addEventListener('orientationchange', () => setTimeout(measureRail, 250));
  measureRail();

  const hud = new NavHud(uiEl, {
    onEnd: () => endNavigation('ended'),
    onRecenter: () => {
      mapView.recenter();
      hud.setRecenterVisible(false);
    },
    onOverview: () => {
      const route = activeRoute();
      if (route) mapView.fitRoute([route], isPortrait());
      window.setTimeout(refreshRecenter, 800);
    },
  });

  const idleBar = document.createElement('div');
  idleBar.className = 'idlebar';
  idleBar.innerHTML = `
    <div class="idlebar__status" hidden></div>
    <div class="idlebar__buttons">
      <button class="icon-btn idlebar__btn" data-act="trips" aria-label="Trips">
        <svg viewBox="0 0 24 24"><path d="M4 19 C4 12 10 12 12 8 C13 6 12 4 12 4 M12 4 L9 6 M12 4 L15 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="5" cy="19" r="2" fill="currentColor"/><circle cx="19" cy="5" r="2" fill="currentColor"/></svg>
      </button>
      <button class="icon-btn idlebar__btn" data-act="share" aria-label="Share my location">
        <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="3.2" fill="currentColor"/><path d="M4.5 12 a7.5 7.5 0 0 1 15 0 M7.5 12 a4.5 4.5 0 0 1 9 0" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/></svg>
      </button>
      <button class="icon-btn idlebar__btn" data-act="settings" aria-label="Settings">
        <svg viewBox="0 0 24 24"><path d="M12 8.5 a3.5 3.5 0 1 0 0 7 a3.5 3.5 0 0 0 0-7 Z M19 12 c0-.6.5-1.2.4-1.8 l-1.8-.6 -.5-1.2 .9-1.7 c-.7-.8-1.2-1.2-2-1.7 l-1.6 1 -1.3-.5 -.7-1.8 h-2.4 l-.7 1.8 -1.3.5 -1.6-1 c-.8.5-1.3 1-2 1.7 l.9 1.7 -.5 1.2 -1.8.6 c-.1.6.4 1.2.4 1.8 s-.5 1.2-.4 1.8 l1.8.6 .5 1.2 -.9 1.7 c.7.8 1.2 1.2 2 1.7 l1.6-1 1.3.5 .7 1.8 h2.4 l.7-1.8 1.3-.5 1.6 1 c.8-.5 1.3-1 2-1.7 l-.9-1.7 .5-1.2 1.8-.6 c.1-.6-.4-1.2-.4-1.8 Z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg>
      </button>
    </div>
    <button class="fab" data-act="search" aria-label="Search destination">
      <svg viewBox="0 0 24 24"><circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" stroke-width="2.4"/><path d="M15.5 15.5 L21 21" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg>
      <span>${t('idle.whereTo')}</span>
    </button>`;
  uiEl.appendChild(idleBar);

  /**
   * Trips are not always planned from where you are standing - the driver may
   * be costing a run that starts at the depot tomorrow morning. When
   * `plannedOrigin` is set the preview routes from there instead of the GPS
   * fix; pressing Start still re-routes from the real position, because
   * turn-by-turn has to begin where the vehicle actually is.
   */
  let plannedOrigin: Place | null = null;
  let searchTarget: 'origin' | 'destination' = 'destination';

  async function setPlannedOrigin(place: Place | null): Promise<void> {
    plannedOrigin = place;
    searchTarget = 'destination';
    setPanel(null);
    await refetchPreviewRoutes();
  }

  const searchPanel = new SearchPanel(
    uiEl,
    () => currentFixLngLat(),
    (place) => {
      if (searchTarget === 'origin') void setPlannedOrigin(place);
      else void pickDestination(place);
    },
    () => setPanel(null),
    () => sharedPlaces,
    async (id) => {
      await authApi.deletePlace(id);
      await loadPlaces();
    },
  );

  const preview = new RoutePreview(uiEl, {
    onGo: startNavigation,
    onCancel: cancelPreview,
    onSelectRoute: selectRoute,
    onToggleAvoid: (key, value) => {
      store.patchSettings({ route: { ...store.get().settings.route, [key]: value } });
      void refetchPreviewRoutes();
    },
    onSwitchVehicle: () => {
      const s = store.get().settings;
      const ids = s.vehicles.map((v) => v.id);
      const next = ids[(ids.indexOf(s.activeVehicleId) + 1) % ids.length];
      store.patchSettings({ activeVehicleId: next });
      void refetchPreviewRoutes();
    },
    onSavePlace: () => {
      const dest = store.get().destination;
      if (!dest) return;
      openSaveDialog(dest.name, dest.center);
    },
    onPickOrigin: () => {
      searchTarget = 'origin';
      setPanel('search');
    },
    onClearOrigin: () => void setPlannedOrigin(null),
    onDismissAdvice: () => {
      advice = null;
      adviceDismissed = true;
      renderPreview();
    },
  });

  /* --------------------------- save place dialog ------------------------ */
  function openSaveDialog(prefill: string, center: LngLat): void {
    const categories: Array<{ id: authApi.PlaceCategory; label: string }> = [
      { id: 'home', label: t('cat.home') },
      { id: 'work', label: t('cat.work') },
      { id: 'showroom', label: t('cat.showroom') },
      { id: 'depot', label: t('cat.depot') },
      { id: 'other', label: t('cat.other') },
    ];
    let selected: authApi.PlaceCategory = 'other';

    const dialog = document.createElement('div');
    dialog.className = 'save-dialog';
    dialog.innerHTML = `
      <div class="save-dialog__card">
        <h3>${t('save.title')}</h3>
        <input class="save-dialog__name" type="text" maxlength="120"
               placeholder="${t('save.name.ph')}" value="${prefill.replace(/"/g, '&quot;')}"/>
        <div class="save-dialog__cats">
          ${categories
            .map(
              (c) =>
                `<button class="chip ${c.id === selected ? 'is-on' : ''}" data-cat="${c.id}">${c.label}</button>`,
            )
            .join('')}
        </div>
        <div class="save-dialog__actions">
          <button class="btn btn--ghost" data-act="cancel">${t('preview.cancel')}</button>
          <button class="btn btn--primary" data-act="save">${t('save.save')}</button>
        </div>
      </div>`;
    uiEl.appendChild(dialog);

    const nameInput = dialog.querySelector<HTMLInputElement>('.save-dialog__name')!;
    dialog.querySelectorAll<HTMLButtonElement>('[data-cat]').forEach((b) =>
      b.addEventListener('click', () => {
        selected = b.dataset.cat as authApi.PlaceCategory;
        dialog.querySelectorAll('[data-cat]').forEach((x) => x.classList.toggle('is-on', x === b));
        // Convenience: picking a category with the default name pre-fills it.
        if (!nameInput.value.trim() || nameInput.value === prefill) {
          const cat = categories.find((c) => c.id === selected);
          if (cat && selected !== 'other') nameInput.value = cat.label;
        }
      }),
    );
    dialog.querySelector('[data-act=cancel]')!.addEventListener('click', () => dialog.remove());
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) dialog.remove();
    });
    dialog.querySelector('[data-act=save]')!.addEventListener('click', () => {
      const name = nameInput.value.trim() || prefill;
      void authApi
        .createPlace(name, center[0], center[1], 'place', selected)
        .then(async () => {
          dialog.remove();
          await loadPlaces();
          renderPreview();
        })
        .catch((err) => alert(err instanceof Error ? err.message : t('place.saveFailed')));
    });
  }

  const settingsPanel = new SettingsPanel(
    uiEl,
    () => currentUser,
    () => setPanel(null),
    () => {
      void authApi.logout().finally(() => location.reload());
    },
  );

  const tripsPanel = new TripsPanel(
    uiEl,
    () => setPanel(null),
    (trip) => {
      setPanel(null);
      void pickDestination({
        id: `trip:${trip.id}`,
        name: trip.destinationName,
        address: '',
        center: trip.destination,
        kind: 'history',
      });
    },
  );

  const spotifyBar = new SpotifyBar(uiEl, spotifyPlayer);

  /**
   * Re-centre control.
   *
   * Shown only while actually navigating, and only once the vehicle has
   * genuinely left the screen - not the instant the map is nudged. A button
   * that appears on every small pan is noise, and one that appears when there
   * is no route to return to is meaningless. The HUD owns the control itself;
   * this only decides when it is warranted.
   */
  function refreshRecenter(): void {
    const st = store.get();
    if (st.mode !== 'navigating' || !st.fix) {
      hud.setRecenterVisible(false);
      return;
    }
    // While the camera is following, the vehicle is on screen by definition.
    const strayed = !mapView.isFollowing && mapView.vehicleOffScreen([st.fix.lng, st.fix.lat]);
    hud.setRecenterVisible(strayed);
  }
  mapView.map.on('moveend', refreshRecenter);

  /* --------------------------- fleet + messaging ------------------------ */

  const fleet = new Fleet({
    onDrivers: (contacts) => fleetBubble.update(contacts),
    onIncoming: (msg) => {
      fleetBubble.showIncoming(msg);
      // Spoken in THIS driver's language - the wire only carried an enum.
      announce(t('msg.incoming.voice', { name: msg.fromName, what: t(`msg.${msg.kind}`) }));
    },
    onReply: (msg) => onMessageReply(msg),
  });

  const fleetBubble = new FleetBubble(uiEl, {
    onSend: (to, kind) => void sendQuickMessage(to, kind),
    onReply: (id, reply) => void fleet.reply(id, reply).catch(() => {}),
  });
  fleetBubble.setUnits(store.get().settings.units);
  fleet.start();

  async function sendQuickMessage(to: number, kind: MessageKind): Promise<void> {
    const name = fleet.roster.find((c) => c.id === to)?.name ?? '';
    try {
      await fleet.send(to, kind);
      statusFlash(t('msg.sent', { name }));
      if (store.get().mode === 'navigating') hud.toast(t('msg.sent', { name }));
    } catch {
      statusFlash(t('msg.sendFailed'));
    }
  }

  function onMessageReply(msg: QuickMessage): void {
    const answer = t(msg.reply === 'yes' ? 'msg.yes' : 'msg.no');
    const line = t('msg.reply.toast', { name: msg.fromName ?? '', answer });
    // The reply is about a question WE asked, so `fromName` is us; use the
    // roster to name the person who actually answered.
    const who = fleet.roster.find((c) => c.id === msg.to)?.name ?? '';
    const text = who ? `${who}: ${answer}` : line;
    if (store.get().mode === 'navigating') hud.toast(text);
    else statusFlash(text);
    announce(t('msg.reply.voice', { name: who, answer }));
  }

  /* ------------------------------ PTT stops ----------------------------- */

  const fuelBubble = new FuelBubble(uiEl, (stop) => void takeFuelDetour(stop));

  /* Live telemetry - everything being recorded, as it happens. */
  let liveSamples = 0;
  let liveRough: number | null = null;
  const livePanel = new LiveDataPanel(uiEl, () => {
    const st = store.get();
    const prog = st.progress;
    return {
      fix: st.fix,
      progress: st.mode === 'navigating' ? prog : null,
      roughness: liveRough,
      tier: prog ? tierForStep(prog.step) : null,
      tripDistance: recorder?.distance ?? 0,
      samples: liveSamples,
      movingSeconds: Math.round(liveMovingSeconds),
      gradeNow: gradeAtCurrentPosition(),
      signalsOnRoute: routeStats[st.selectedRouteIndex]?.trafficSignals ?? null,
      fleetCount: fleet.roster.length,
      units: st.settings.units,
    };
  });
  let liveMovingSeconds = 0;
  let lastFixAt = 0;

  /** Gradient under the wheels right now, from the loaded elevation profile. */
  function gradeAtCurrentPosition(): number | null {
    const prog = store.get().progress;
    if (!elevation || !prog) return null;
    const at = prog.snapped.distanceAlongRoute;
    const s1 = elevation.samples;
    let lo = 0;
    for (let i = 1; i < s1.length; i++) {
      if (s1[i][0] > at) break;
      lo = i;
    }
    const a = s1[lo];
    const b = s1[Math.min(s1.length - 1, lo + 2)];
    const run = b[0] - a[0];
    if (run <= 0) return null;
    return Math.round(((b[1] - a[1]) / run) * 1000) / 10;
  }

  /* Everything worth knowing about the journey, in one place. */
  const routeInfo = new RouteInfoSheet(uiEl, () => {
    const st = store.get();
    return {
      route: st.routes[st.selectedRouteIndex] ?? navigator_?.route ?? null,
      stats: routeStats[st.selectedRouteIndex] ?? routeStats[0] ?? null,
      elevation,
      progress: st.mode === 'navigating' ? st.progress : null,
      fuelStop: fuelStops[0] ?? null,
      units: st.settings.units,
      speeds: st.settings.speeds,
      vehicleName: activeVehicle(st.settings).name,
    };
  });
  let fuelStops: FuelStop[] = [];
  let nearestFuel: FuelStop | null = null;
  let allFuel: FuelStop[] = [];
  let fuelSearchedAt = -Infinity;
  let fuelSearchInFlight = false;
  /** The route we were on before a fuel detour, so Undo can put it back. */
  let routeBeforeDetour: Route | null = null;

  async function refreshFuelStop(along: number): Promise<void> {
    const route = navigator_?.route;
    if (!route || fuelSearchInFlight) return;
    // Re-query only every 5 km, or once the chosen stop is behind us.
    const stale = fuelStops[0] ? along > fuelStops[0].along - 300 : false;
    if (!stale && Math.abs(along - fuelSearchedAt) < 5000) return;

    fuelSearchInFlight = true;
    fuelSearchedAt = along;
    try {
      const stops = await findFuelStopsAhead({
        route,
        along,
        drivingSide: store.get().progress?.drivingSide ?? 'left',
      });
      // Keep a couple: the sheet offers a second choice when the nearest is
      // served by a frontage road.
      fuelStops = stops.ptt.slice(0, 2);
      nearestFuel = stops.nearest;
      allFuel = stops.all;
    } catch {
      /* leave the previous stop in place rather than flickering the bubble */
    } finally {
      fuelSearchInFlight = false;
    }
  }

  async function takeFuelDetour(stop: FuelStop): Promise<void> {
    const dest = store.get().destination;
    const fix = store.get().fix;
    if (!dest || !fix || !navigator_) return;

    hud.toast(t('fuel.detour', { name: stop.name }), true);
    try {
      const routes = await fetchRoutes({
        origin: [fix.lng, fix.lat],
        destination: dest.center,
        // Routing THROUGH the station means the trip carries on to the real
        // destination by itself once we pull out - nothing to unwind.
        via: [stop.location],
        heading: fix.heading,
        settings: { ...store.get().settings, route: { ...store.get().settings.route, alternatives: false } },
      });
      const s2 = store.get().settings;
      const detour = applyPace(routes, s2.speeds, s2.signalSeconds)[0];
      routeBeforeDetour = navigator_.route;
      navigator_.replaceRoute(detour);
      routeRough = roughnessOfRoute(detour, roughIndex);
      routeCumulative = cumulativeDistances(detour.geometry.coordinates);
      routeStats = [computeRouteStats(detour)];
      store.set({ routes: [detour], selectedRouteIndex: 0 });
      mapView.drawRoute(detour);
      fuelStops = [];
    nearestFuel = null;
    allFuel = [];
      fuelBubble.hide();
      hud.toast(t('fuel.detour', { name: stop.name }), false, {
        label: t('fuel.undo'),
        onAction: () => undoFuelDetour(),
      });
    } catch {
      hud.toast(t('fuel.failed'));
    }
  }

  function undoFuelDetour(): void {
    const previous = routeBeforeDetour;
    if (!previous || !navigator_) return;
    routeBeforeDetour = null;
    navigator_.replaceRoute(previous);
    routeCumulative = cumulativeDistances(previous.geometry.coordinates);
    routeStats = [computeRouteStats(previous)];
    store.set({ routes: [previous], selectedRouteIndex: 0 });
    mapView.drawRoute(previous);
    fuelSearchedAt = -Infinity;
    hud.toast(t('fuel.restored'));
  }

  idleBar.querySelector('[data-act=search]')!.addEventListener('click', () => {
    primeSpeech(); // user gesture unlocks TTS for later
    setPanel('search');
  });
  idleBar.querySelector('[data-act=settings]')!.addEventListener('click', () => setPanel('settings'));
  idleBar.querySelector('[data-act=trips]')!.addEventListener('click', () => setPanel('trips'));
  idleBar.querySelector('[data-act=share]')!.addEventListener('click', () => {
    const f = store.get().fix;
    if (!f) {
      statusFlash(t('idle.noFix'));
      return;
    }
    const now = new Date();
    const label = `${currentUser?.name ?? 'Driver'} · ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    void authApi
      .createPlace(label, f.lng, f.lat, 'shared-location')
      .then(async () => {
        await loadPlaces();
        statusFlash(t('idle.shared'));
      })
      .catch(() => statusFlash(t('idle.shareFailed')));
  });

  let statusFlashTimer: number | null = null;
  function statusFlash(message: string): void {
    statusEl.textContent = message;
    statusEl.hidden = false;
    if (statusFlashTimer !== null) clearTimeout(statusFlashTimer);
    statusFlashTimer = window.setTimeout(() => {
      statusEl.hidden = true;
    }, 3500);
  }

  function setPanel(panel: 'search' | 'settings' | 'trips' | null): void {
    searchPanel.close();
    settingsPanel.close();
    tripsPanel.close();
    if (panel === 'search') searchPanel.open();
    if (panel === 'settings') void settingsPanel.open();
    if (panel === 'trips') void tripsPanel.open();
    store.set({ panel });
  }

  /* -------------------------------- GPS -------------------------------- */
  const statusEl = idleBar.querySelector('.idlebar__status') as HTMLElement;

  trace('ui:ok');
  const gps = new GpsWatcher({
    onFix: (fix) => {
      const first = !store.get().located;
      store.set({ fix, located: true, gpsError: null });
      statusEl.hidden = true;
      if (first) mapView.jumpTo([fix.lng, fix.lat], 15.5);

      fleet.setFix(fix);
      refreshRecenter();
      const mode = store.get().mode;
      if (mode === 'navigating') {
        onNavFix(fix);
      } else {
        mapView.updatePuck(fix);
      }
    },
    onError: (message) => {
      store.set({ gpsError: message });
      statusEl.textContent = message;
      statusEl.hidden = false;
    },
  });
  gps.start();

  function currentFixLngLat(): LngLat | null {
    const f = store.get().fix;
    return f ? [f.lng, f.lat] : null;
  }

  /* ------------------------------ routing ------------------------------ */
  let routeAbort: AbortController | null = null;
  /** Traffic-light / turn / toll counts for the current candidate routes. */
  let routeStats: RouteStats[] = [];
  let advice: RouteAdvice | null = null;
  /** Gradient profile of the selected route for the active vehicle. */
  let elevation: ElevationProfile | null = null;
  let adviceLoading = false;
  let adviceDismissed = false;
  let lastAdviceKey = '';
  let adviceAbort: AbortController | null = null;

  async function pickDroppedPin(point: LngLat): Promise<void> {
    if (store.get().mode === 'navigating') return;
    const place = await reverseGeocode(point);
    void pickDestination(place);
  }

  async function pickDestination(place: Place): Promise<void> {
    setPanel(null);
    advice = null;
    adviceDismissed = false;
    lastAdviceKey = '';
    store.set({ destination: place, mode: 'preview', routes: [], routeError: null });
    mapView.setDestination(place.center);
    void addRecent(place);
    await refetchPreviewRoutes();
  }

  async function refetchPreviewRoutes(): Promise<void> {
    const state = store.get();
    const dest = state.destination;
    const origin = plannedOrigin ? plannedOrigin.center : currentFixLngLat();
    if (!dest) return;
    if (!origin) {
      store.set({ routeError: t('preview.waitingFix') });
      preview.render({
        destination: dest,
        routes: [],
        stats: [],
        selectedIndex: 0,
        loading: false,
        error: t('preview.waitingFix'),
      });
      return;
    }

    routeAbort?.abort();
    routeAbort = new AbortController();
    store.set({ routing: true, routeError: null });
    preview.render({
      destination: dest,
      routes: [],
      stats: [],
      selectedIndex: 0,
      loading: true,
      error: null,
    });

    try {
      // Corridors this driver has used before for the same destination, so a
      // route they know is better than the engine's gets offered explicitly.
      const past = await listTrips(80);
      const learned = learnedVias(past, origin, dest.center).map((c) => c.vias);

      const fetched = await fetchRouteAlternatives(
        {
          origin,
          destination: dest.center,
          heading: plannedOrigin ? null : (state.fix?.heading ?? null),
          settings: store.get().settings,
          signal: routeAbort.signal,
        },
        learned,
      );
      // Re-time and re-rank against the driver's own cruising speeds and the
      // cost of a red light, so the option list is ordered by how long the
      // journey will actually take THEM.
      const s = store.get().settings;
      const routes = applyPace(fetched, s.speeds, s.signalSeconds);
      routeStats = statsFor(routes);
      store.set({ routes, selectedRouteIndex: 0, routing: false });
      routeRough = roughnessOfRoute(routes[0], roughIndex);
      elevation = null;
      renderPreview();
      mapView.fitRoute(routes, isPortrait());
      routeInfo.setVisible(true);
      void refreshElevation();
      void requestAdvice();
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      const message = err instanceof Error ? err.message : 'Routing failed.';
      routeStats = [];
      store.set({ routing: false, routeError: message, routes: [] });
      renderPreview();
    }
  }

  /**
   * Ask the advisor for a second opinion. Entirely best-effort and entirely
   * off the critical path - the driver can hit GO before it ever answers, and
   * a server with no API key configured simply never shows the card.
   */
  async function requestAdvice(): Promise<void> {
    const s = store.get();
    const dest = s.destination;
    if (!dest || s.routes.length < 2 || adviceDismissed) return;

    const input = {
      destination: dest,
      routes: s.routes,
      stats: routeStats,
      trips: await listTrips(60),
      settings: s.settings,
      elevation,
    };
    const key = adviceKey(input);
    if (key === lastAdviceKey) return; // same question, already answered
    lastAdviceKey = key;

    adviceAbort?.abort();
    adviceAbort = new AbortController();
    adviceLoading = true;
    renderPreview();

    try {
      const result = await fetchAdvice(input, adviceAbort.signal);
      if (store.get().destination !== dest) return; // driver moved on
      advice = result;
    } catch {
      advice = null; // never surface advisor failures to a driver
    } finally {
      adviceLoading = false;
      renderPreview();
    }
  }

  function renderPreview(): void {
    const s = store.get();
    if (!s.destination || s.mode !== 'preview') return;
    preview.render({
      destination: s.destination,
      routes: s.routes,
      stats: routeStats,
      selectedIndex: s.selectedRouteIndex,
      loading: s.routing,
      error: s.routeError,
      isSaved: isDestSaved(),
      advice,
      adviceLoading,
      elevation,
      vehicleName: activeVehicle(s.settings).name,
      originName: plannedOrigin?.name ?? null,
      roughness: routeRough,
    });
    const selected = s.routes[s.selectedRouteIndex];
    mapView.drawRoute(selected ?? null);
    mapView.drawAlternatives(s.routes.filter((_, i) => i !== s.selectedRouteIndex));
  }

  function selectRoute(index: number): void {
    store.set({ selectedRouteIndex: index });
    const picked = store.get().routes[index];
    if (picked) routeRough = roughnessOfRoute(picked, roughIndex);
    elevation = null;
    renderPreview();
    void refreshElevation();
  }

  /**
   * Work out how hard the selected route is to climb, for THIS vehicle.
   * Runs after the routes are already on screen: it costs a handful of terrain
   * tiles and must never delay the driver seeing their options.
   */
  async function refreshElevation(): Promise<void> {
    const s = store.get();
    const route = s.routes[s.selectedRouteIndex];
    if (!route) return;
    const forRoute = route._id;
    try {
      const profile = await analyseElevation(route, activeVehicle(s.settings));
      // The driver may have picked a different route while we were fetching.
      if (store.get().routes[store.get().selectedRouteIndex]?._id !== forRoute) return;
      elevation = profile;
      renderPreview();
      routeInfo.refresh();
      void requestAdvice();
    } catch {
      /* terrain unavailable - the route list simply shows no gradient note */
    }
  }

  function activeRoute(): Route | null {
    const s = store.get();
    return s.routes[s.selectedRouteIndex] ?? null;
  }

  function cancelPreview(): void {
    routeInfo.setVisible(false);
    plannedOrigin = null;
    searchTarget = 'destination';
    routeAbort?.abort();
    preview.hide();
    mapView.drawRoute(null);
    mapView.drawAlternatives([]);
    mapView.setDestination(null);
    store.set({ mode: 'idle', destination: null, routes: [], routeError: null });
    const f = store.get().fix;
    if (f) mapView.jumpTo([f.lng, f.lat], 15.5);
  }

  /* ----------------------------- navigation ---------------------------- */
  let navigator_: RouteNavigator | null = null;
  let rerouteInFlight = false;
  let lastRerouteAt = 0;
  let lastRerouteAnnounceAt = 0;
  let recorder: TripRecorder | null = null;
  /** Where we last looked up the village name, so we don't ask every fix. */
  let localityAt: LngLat | null = null;
  let localityInFlight = false;

  /* Road-surface knowledge accumulated from our own drives. */
  const roughMeter = new RoughnessMeter();
  let roughIndex: RoughIndex = { cells: new Map(), count: 0 };
  let routeRough: RouteRoughness | null = null;
  let warnedRoughAt = -1;

  async function refreshRoughIndex(): Promise<void> {
    try {
      const trips = await listTrips(120);
      roughIndex = buildRoughIndex(trips);
      await refreshDrivenOverlay(trips);
    } catch {
      /* no history yet - the overlay and warnings simply stay quiet */
    }
  }

  /** Redraw the "roads we've driven" overlay in whichever mode is selected. */
  async function refreshDrivenOverlay(preloaded?: Awaited<ReturnType<typeof listTrips>>): Promise<void> {
    const mode = store.get().settings.drivenOverlay;
    if (mode === 'off') {
      mapView.clearDrivenTracks();
      return;
    }
    const trips = preloaded ?? (await listTrips(120));
    const tracks = trips.map((trip) =>
      (trip.track ?? [])
        .map((raw) => {
          const p = readPoint(raw);
          // Third value is whatever the overlay is colouring by.
          return [p[0], p[1], mode === 'rough' ? p[6] : p[3]] as [number, number, number];
        })
        // Thin long tracks: at overlay scale every third point is plenty.
        .filter((_, i) => i % 3 === 0),
    );
    mapView.setDrivenTracks(tracks, mode === 'rough' ? 'rough' : 'speed');
  }

  void refreshRoughIndex();
  select(
    (st) => st.settings.drivenOverlay,
    () => void refreshDrivenOverlay(),
    { immediate: false },
  );
  let routeCumulative: number[] = [];

  function startNavigation(): void {
    const route = activeRoute();
    const dest = store.get().destination;
    if (!route || !dest) return;

    // A route planned from somewhere else cannot be driven turn-by-turn from
    // here. Drop the planned origin and re-route from the vehicle's actual
    // position; the driver sees the corrected route immediately.
    const fixNow = currentFixLngLat();
    if (plannedOrigin && fixNow && distance(plannedOrigin.center, fixNow) > 250) {
      plannedOrigin = null;
      void refetchPreviewRoutes();
      return;
    }
    plannedOrigin = null;

    primeSpeech();
    // Motion access on iOS must be asked for from a user gesture; this is one.
    void RoughnessMeter.requestPermission().then((ok) => {
      if (ok) roughMeter.start();
    });
    preview.hide();
    mapView.drawAlternatives([]);
    mapView.drawRoute(route);
    mapView.startNavigation();
    hud.show();
    appEl.dataset.mode = 'navigating';
    store.set({ mode: 'navigating' });
    routeInfo.setVisible(true);
    livePanel.setVisible(true);
    liveSamples = 0;
    liveMovingSeconds = 0;
    lastFixAt = 0;
    if (store.get().settings.keepAwake) enableWakeLock();

    routeCumulative = cumulativeDistances(route.geometry.coordinates);
    fleet.setDestinationName(dest.name);
    fuelStops = [];
    nearestFuel = null;
    allFuel = [];
    fuelSearchedAt = -Infinity;
    routeBeforeDetour = null;

    navigator_ = new RouteNavigator(
      route,
      {
        onAnnounce: announce,
        onOffRoute: () => void handleOffRoute(),
        onArrive: () => endNavigation('arrived'),
      },
      // Announcements are composed by the navigator, so it needs to know which
      // language and units to compose them in.
      () => ({ units: store.get().settings.units, lang: getLang() }),
    );

    const s = store.get().settings;
    if (s.logTrips) {
      const f = store.get().fix;
      const stats = routeStats[store.get().selectedRouteIndex] ?? computeRouteStats(route);
      recorder = new TripRecorder({
        destinationName: dest.name,
        destination: dest.center,
        originName: 'Current location',
        origin: f ? [f.lng, f.lat] : null,
        vehicle: activeVehicle(s),
        predictedDuration: route.duration,
        predictedDistance: route.distance,
        trafficSignals: stats.trafficSignals,
      });
    }

    announce(t('nav.voice.starting', { name: dest.name }));
  }

  function onNavFix(fix: Fix): void {
    if (!navigator_) return;
    const progress = navigator_.update(fix);
    store.set({ progress });

    const s = store.get().settings;
    maneuverCard.update(progress, s.units, fix.speed ?? 0);
    if (s.adaptiveCamera || mapView.isFollowing) mapView.driveCamera(fix, progress);
    else mapView.updatePuck(fix, progress.snapped.location);

    // Overspeed check against the posted limit for the current segment.
    let overspeed = false;
    if (s.speedWarnOffset > 0 && fix.speed != null) {
      const limit = speedLimitAt(progress.snapped.distanceAlongRoute);
      if (limit != null) {
        overspeed = fix.speed * 3.6 > limit + s.speedWarnOffset;
      }
    }
    hud.update(progress, fix.speed, s.units, overspeed);
    routeInfo.refresh();

    liveRough = roughMeter.read();
    liveSamples++;
    if (lastFixAt && (fix.speed ?? 0) > 1) {
      liveMovingSeconds += Math.min(30, (fix.timestamp - lastFixAt) / 1000);
    }
    lastFixAt = fix.timestamp;
    livePanel.refresh();

    roughMeter.setSpeed((fix.speed ?? 0) * 3.6);

    // Live warning before a stretch we already know is badly broken.
    if (routeRough?.known) {
      const ahead = roughAhead(routeRough, progress.snapped.distanceAlongRoute, 900);
      if (ahead && ahead.from !== warnedRoughAt) {
        warnedRoughAt = ahead.from;
        const away = Math.max(0, ahead.from - progress.snapped.distanceAlongRoute);
        hud.toast(t('rough.aheadToast', { d: formatDistanceShort(away, s.units) }));
        announce(t('rough.aheadVoice'));
      }
    }

    // Village / sub-district, so an unnamed road still says where you are.
    // Refreshed every ~2 km rather than per fix.
    if (!localityInFlight && (!localityAt || distance(localityAt, [fix.lng, fix.lat]) > 2000)) {
      localityInFlight = true;
      localityAt = [fix.lng, fix.lat];
      void reverseLocality([fix.lng, fix.lat])
        .then((name) => maneuverCard.setLocality(name))
        .catch(() => {})
        .finally(() => {
          localityInFlight = false;
        });
    }

    // Next PTT along the route, and how far to it.
    void refreshFuelStop(progress.snapped.distanceAlongRoute);
    if ((fuelStops.length || nearestFuel || allFuel.length) && navigator_) {
      const at = progress.snapped.distanceAlongRoute;
      const route = navigator_.route;
      const toChoice = (stop: FuelStop): FuelChoice => ({
        stop,
        remaining: Math.max(0, stop.along - at),
        eta: etaToStop(route, stop, at),
      });
      fuelBubble.update(
        fuelStops.map(toChoice),
        nearestFuel ? toChoice(nearestFuel) : null,
        allFuel.map(toChoice),
        s.units,
      );
    } else {
      fuelBubble.hide();
    }

    // Trip log: the recorder handles its own sampling cadence and rolls the
    // drive up into segments as it goes.
    recorder?.add(fix, roadLabel(progress.step, getLang()), tierForStep(progress.step), roughMeter.read());
  }

  function speedLimitAt(distanceAlong: number): number | null {
    const route = navigator_?.route;
    const max = route?.legs[0]?.annotation?.maxspeed;
    if (!max?.length || routeCumulative.length < 2) return null;
    // Binary search the geometry vertex for this distance.
    let lo = 0;
    let hi = routeCumulative.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (routeCumulative[mid] < distanceAlong) lo = mid + 1;
      else hi = mid;
    }
    const seg = Math.max(0, lo - 1);
    const entry = max[Math.min(seg, max.length - 1)];
    if (!entry || entry.unknown || entry.none || entry.speed == null) return null;
    return entry.unit === 'mph' ? entry.speed * 1.609344 : entry.speed;
  }

  async function handleOffRoute(): Promise<void> {
    if (rerouteInFlight || !navigator_) return;
    // Cooldown: repeated triggers (bad GPS patches) must not hammer the
    // routing API or chant "route updated" at the driver.
    if (Date.now() - lastRerouteAt < 20_000) return;
    const dest = store.get().destination;
    const fix = store.get().fix;
    if (!dest || !fix) return;

    lastRerouteAt = Date.now();
    rerouteInFlight = true;
    store.set({ rerouting: true });
    hud.toast(t('nav.rerouting'), true);

    try {
      const routes = await fetchRoutes({
        origin: [fix.lng, fix.lat],
        destination: dest.center,
        heading: fix.heading,
        settings: { ...store.get().settings, route: { ...store.get().settings.route, alternatives: false } },
      });
      const rs = store.get().settings;
      const route = applyPace(routes, rs.speeds, rs.signalSeconds)[0];
      navigator_.replaceRoute(route);
      routeCumulative = cumulativeDistances(route.geometry.coordinates);
      routeStats = [computeRouteStats(route)];
      recorder?.noteReroute();
      store.set({ routes: [route], selectedRouteIndex: 0 });
      mapView.drawRoute(route);
      hud.clearToast();
      if (Date.now() - lastRerouteAnnounceAt > 45_000) {
        lastRerouteAnnounceAt = Date.now();
        announce(t('nav.voice.updated'));
      }
    } catch {
      hud.toast(store.get().online ? t('nav.rerouteFailed') : t('nav.rerouteOffline'));
    } finally {
      rerouteInFlight = false;
      store.set({ rerouting: false });
    }
  }

  function endNavigation(reason: 'arrived' | 'ended'): void {
    const dest = store.get().destination;
    if (reason === 'arrived' && dest) {
      announce(t('nav.voice.arrived', { name: dest.name }));
      hud.toast(t('nav.arrivedToast'));
    } else {
      stopSpeaking();
    }

    if (recorder) {
      const done = recorder.finish(activeVehicle(store.get().settings));
      if (done) {
        void saveTrip(done);
        void authApi.syncTrip(done).catch(() => {});
      }
      recorder = null;
    }

    navigator_ = null;
    roughMeter.stop();
    routeRough = null;
    warnedRoughAt = -1;
    void refreshRoughIndex();
    disableWakeLock();
    maneuverCard.hide();
    fuelBubble.hide();
    fuelStops = [];
    nearestFuel = null;
    allFuel = [];
    routeBeforeDetour = null;
    fleet.setDestinationName(null);
    hud.hide();
    mapView.stopNavigation();
    mapView.drawRoute(null);
    mapView.setDestination(null);
    appEl.dataset.mode = 'idle';
    routeInfo.setVisible(false);
    livePanel.setVisible(false);
    store.set({ mode: 'idle', destination: null, routes: [], progress: null });
    const f = store.get().fix;
    if (f) mapView.jumpTo([f.lng, f.lat], 15.5);
  }

  /* ------------------------------ version tag --------------------------- */
  const versionEl = document.createElement('div');
  versionEl.className = 'version-tag';
  versionEl.textContent = `v${APP_VERSION}`;
  uiEl.appendChild(versionEl);

  /* ------------------------- viewport plumbing -------------------------- */
  function isPortrait(): boolean {
    return window.innerHeight >= window.innerWidth;
  }
  window.addEventListener('resize', () => mapView.resize());
  window.addEventListener('orientationchange', () => setTimeout(() => mapView.resize(), 250));

  trace('done');
  // UI exists from here on - later async errors must not blank the screen.
  traceDone();
}

void gate();
